import { useEffect } from "react";
import { motion } from "motion/react";
import { Sparkles, Zap, PiggyBank, Camera, ShieldCheck, FileText } from "lucide-react";

const InstagramIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/>
  </svg>
);
import { useLang, t } from "@/lib/i18n";
import { withLangPrefix } from "@/lib/i18n-core";
import "./landing.css";

import { Navbar } from "./components/Navbar";
import { Hero } from "./components/Hero";
import { Pricing } from "./components/Pricing";
import { Contact } from "./components/Contact";
import { WaitlistCTA } from "./components/WaitlistCTA";
import { FaqItem } from "./components/FaqItem";
import { BrandNameSm } from "./components/BrandName";
import ReferralPromoBanner from "../../components/ReferralPromoBanner";

export default function LandingPage() {
  const { lang, setLang } = useLang();
  const tr = t(lang);

  useEffect(() => {
    if (lang === 'zh') import('@fontsource/noto-sans-sc/900.css');
  }, [lang]);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen selection:bg-landing-accent/20 font-landing-sans text-[#2d3435] antialiased">
      <ReferralPromoBanner surface="landing" />
      <Navbar setActiveSection={scrollToSection} lang={lang} setLang={setLang} tr={tr} />

      <main>
        <Hero scrollTo={scrollToSection} tr={tr} lang={lang} />

        {/* Features Section */}
        <section id="services" className="py-32 px-6 md:px-12 bg-landing-section-bg">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-4 flex flex-col justify-center">
              <h2 className="text-4xl md:text-5xl font-normal mb-8 leading-tight font-landing-serif">{tr.features.title}</h2>
              <p className="text-black/60 leading-relaxed text-lg">{tr.features.desc}</p>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-8">
              {[
                { icon: <Sparkles className="w-8 h-8 text-landing-accent" />, ...tr.features.cards[0], gradient: false },
                { icon: <Zap className="w-8 h-8 text-white" />, ...tr.features.cards[1], gradient: true },
                { icon: <PiggyBank className="w-8 h-8 text-landing-accent" />, ...tr.features.cards[2], gradient: false },
                { icon: <Camera className="w-8 h-8 text-landing-accent" />, ...tr.features.cards[3], gradient: false }
              ].map((f, i) => (
                <motion.div key={i} whileHover={{ y: -5 }} className={`${f.gradient ? "btn-gradient text-white" : "bg-white"} p-10 md:p-12 sm:aspect-square flex flex-col shadow-sm rounded-2xl`}>
                  <div>{f.icon}</div>
                  <div className="mt-auto">
                    <h3 className="text-2xl mb-3 sm:min-h-[3.5rem] flex items-end font-landing-sans">{f.title}</h3>
                    <p className={`text-sm ${f.gradient ? 'opacity-90' : 'text-black/60'} leading-relaxed sm:min-h-[4.5rem]`}>{f.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <Pricing scrollTo={scrollToSection} tr={tr} lang={lang} />

        {/* Testimonial */}
        <section className="py-40 px-6 text-center max-w-4xl mx-auto">
          <div className="w-16 h-px bg-landing-accent mx-auto mb-12"></div>
          <p className="text-3xl md:text-5xl font-landing-serif leading-tight italic text-black/80">{tr.testimonial.quote}</p>
          <div className="w-16 h-px bg-landing-purple mx-auto mt-12 mb-6"></div>
          <span className="label-vogue text-black/50">{tr.testimonial.author}</span>
        </section>

        {/* Gallery */}
        <section id="gallery" className="pb-32 px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
              <h2 className="text-5xl md:text-7xl font-normal font-landing-serif">{tr.gallery.title}</h2>
              <p className="label-vogue text-black/50 border-b border-black/10 pb-2">{tr.gallery.subtitle}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                { src: "/landing/gallery-1.webp", alt: "AI-generated travel fashion photo with model and luggage in Santorini" },
                { src: "/landing/gallery-2.webp", alt: "AI-generated jewelry campaign featuring gold necklace and earrings" },
                { src: "/landing/gallery-3.webp", alt: "AI-generated beauty campaign with foundation product placement" },
                { src: "/landing/gallery-4.webp", alt: "AI-generated western boho fashion editorial with fringe details" },
                { src: "/landing/gallery-5.webp", alt: "AI-generated Chinese fashion editorial with modern qipao silhouette" },
                { src: "/landing/gallery-6.webp", alt: "AI-generated luxury handbag campaign in classical interior" },
              ].map((img, i) => (
                <div key={i} className="group overflow-hidden bg-landing-section-bg rounded-2xl aspect-[3/4]">
                  <img src={img.src} alt={img.alt} loading="lazy" decoding="async" className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-700 scale-100 group-hover:scale-105" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-32 px-6 md:px-12 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-5xl md:text-6xl font-normal mb-16 text-center font-landing-serif">{tr.faq.title}</h2>
            <div className="divide-y divide-black/10">
              {tr.faq.items.map((item, i) => (
                <FaqItem key={i} question={item.q} answer={item.a} />
              ))}
            </div>
          </div>
        </section>

        <Contact tr={tr} />
        <WaitlistCTA tr={tr} lang={lang} />
      </main>

      {/* Footer */}
      <footer className="bg-white py-20 px-6 md:px-12 border-t border-black/5">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-16">
          <div className="space-y-6">
            <div className="flex items-center gap-2.5">
              <img src="/logo.webp" alt="POPWIZ" className="w-8 h-8 object-contain" />
              <BrandNameSm lang={lang} />
            </div>
            <p className="text-[10px] label-vogue text-black/40 max-w-[200px] leading-relaxed">{tr.footer.tagline}</p>
            <div className="text-[10px] label-vogue text-black/30 leading-relaxed">
              <p>CONFLUX LABS LTD</p>
              <p>71-75 Shelton Street, Covent Garden</p>
              <p>London, WC2H 9JQ, United Kingdom</p>
            </div>
          </div>
          <div className="flex flex-col space-y-4">
            <span className="label-vogue text-black/80 mb-2">{tr.footer.connect}</span>
            <a href="https://www.instagram.com/popwiz_ai?igsh=MTJ6bTJrcHkyenNoNw==" target="_blank" rel="noopener noreferrer" className="label-vogue text-black/40 hover:text-landing-accent transition-colors flex items-center gap-2"><InstagramIcon className="w-3 h-3" /> Instagram</a>
          </div>
          <div className="flex flex-col space-y-4">
            <span className="label-vogue text-black/80 mb-2">{tr.footer.inquiries}</span>
            <button onClick={() => scrollToSection("contact")} className="label-vogue text-black/40 hover:text-landing-accent transition-colors text-left">{tr.footer.contactLink}</button>
            <button onClick={() => scrollToSection("contact")} className="label-vogue text-black/40 hover:text-landing-accent transition-colors text-left">{tr.footer.newsletter}</button>
          </div>
          <div className="flex flex-col space-y-4">
            <span className="label-vogue text-black/80 mb-2">{tr.footer.legal}</span>
            <a href={withLangPrefix('/app/privacy', lang)} className="label-vogue text-black/40 hover:text-landing-accent transition-colors text-left flex items-center gap-2"><ShieldCheck className="w-3 h-3" /> {tr.footer.privacy}</a>
            <a href={withLangPrefix('/app/terms', lang)} className="label-vogue text-black/40 hover:text-landing-accent transition-colors text-left flex items-center gap-2"><FileText className="w-3 h-3" /> {tr.footer.terms}</a>
            <div className="mt-8 text-[10px] label-vogue text-black/30 space-y-2">
              <p>{tr.footer.payment}</p>
              <p>{tr.footer.copyright}</p>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
}
